import React, { useEffect, useState } from 'react';
import { getClientNotes, saveClientNotes } from '../../utils/clients';
import * as ds from '../../constants/dashboardStyles';

const styles = {
  wrap: {
    marginTop: 20,
  },
  toggle: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #ececE6',
    background: '#f9f9f7',
    color: '#555',
    fontSize: '0.85rem',
    cursor: 'pointer',
    textAlign: 'left',
  },
  preview: {
    flex: 1,
    minWidth: 0,
    color: '#999',
    fontSize: '0.8rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  label: {
    fontWeight: 600,
    flexShrink: 0,
  },
  chevron: {
    color: '#aaa',
    flexShrink: 0,
  },
  body: {
    padding: '14px',
    border: '1px solid #ececE6',
    borderTop: 'none',
    borderRadius: '0 0 10px 10px',
    background: '#fff',
  },
  textarea: {
    ...ds.textarea,
    minHeight: 110,
    marginBottom: 10,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  save: {
    padding: '8px 16px',
    borderRadius: 8,
    border: 'none',
    background: '#111',
    color: '#fff',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  disabled: ds.disabled,
  state: {
    fontSize: '0.78rem',
    color: '#888',
  },
  saved: {
    color: '#1a7f37',
    fontWeight: 600,
  },
  error: ds.error,
  privacy: {
    fontSize: '0.75rem',
    color: '#999',
    margin: '10px 0 0',
  },
};

// Private notes on a client — anything worth remembering that isn't a quote or
// an invoice. Collapsed by default so it doesn't sit between you and the work.
export default function ClientNotes({ clientEmail }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clientEmail) return;
    setOpen(false);
    setStatus('');
    getClientNotes(clientEmail)
      .then((value) => { setNotes(value); setSaved(value); })
      .catch((err) => setError(err.message.replace('Firebase: ', '')));
  }, [clientEmail]);

  const dirty = notes !== saved;

  const save = async () => {
    setError('');
    setStatus('saving');
    try {
      await saveClientNotes(clientEmail, notes);
      setSaved(notes);
      setStatus('saved');
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
      setStatus('');
    }
  };

  // One line of the note on the collapsed row, so you can tell at a glance
  // whether there's anything worth opening.
  const preview = saved.trim().split('\n')[0] || 'Nothing noted yet';

  return (
    <div style={styles.wrap}>
      <button
        type="button"
        style={{ ...styles.toggle, ...(open ? { borderRadius: '10px 10px 0 0' } : {}) }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span style={styles.label}>Notes</span>
        <span style={styles.preview}>{preview}</span>
        <span style={styles.chevron}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={styles.body}>
          <textarea
            style={styles.textarea}
            placeholder="Anything worth remembering — who to speak to, how they like to work, what they've asked for before."
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setStatus(''); }}
          />
          <div style={styles.row}>
            <button
              type="button"
              style={{ ...styles.save, ...(dirty ? {} : styles.disabled) }}
              onClick={save}
              disabled={!dirty || status === 'saving'}
            >
              {status === 'saving' ? 'Saving…' : 'Save notes'}
            </button>
            {status === 'saved' && !dirty && <span style={{ ...styles.state, ...styles.saved }}>Saved</span>}
            {dirty && <span style={styles.state}>Unsaved changes</span>}
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <p style={styles.privacy}>Only visible to us — the client never sees these.</p>
        </div>
      )}
    </div>
  );
}
